import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["src", "onIntersectionChange"];
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { callIfPossible } from 'UIComponents/core/Functions';
import UIIntersectionObserver from 'UIComponents/observer/UIIntersectionObserver';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIModal from 'UIComponents/dialog/UIModal';
import UIOverlayContent from 'UIComponents/overlay/UIOverlayContent';
import ResponsiveRenderer from 'developer-docs-shared-components/components/ResponsiveRenderer';
import { mediaQueries, MOBILE_MENU_BREAKPOINT } from 'developer-docs-shared-components/utils/mediaQueries';
import { NEXT_COMPONENT_SPACING } from 'developer-docs-shared-components/config/cssConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const Img = styled.img.withConfig({
  displayName: "Image__Img",
  componentId: "sc-1w1e2bh-0"
})(["max-width:100%;margin:", "px auto;border-radius:3px;box-shadow:0 4px 8px 0 rgb(0 0 0 / 10%),0 6px 20px 0 rgb(0 0 0 / 1%);display:block;will-change:opacity;transition:opacity 0.6s ease,box-shadow 0.6s ease;opacity:", ";", "{cursor:", ";&:hover{box-shadow:", ";}}"], NEXT_COMPONENT_SPACING, ({
  $isIntersecting
}) => $isIntersecting ? 1 : 0, mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT), ({
  context
}) => context === 'document' ? 'pointer' : null, ({
  context
}) => context === 'document' ? '0 4px 8px 0 rgb(0 0 0 / 20%), 0 6px 20px 0 rgb(0 0 0 / 19%)' : null);
const OverlayContentWrapper = styled.div.withConfig({
  displayName: "Image__OverlayContentWrapper",
  componentId: "sc-1w1e2bh-1"
})(["width:100%;text-align:center;"]);

/**
 * Lazy-loaded image in an MDX doc.
 */
function Image(_ref) {
  let {
      src,
      onIntersectionChange
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  // Empty by default to prevent loading
  const [lazyLoadedSrc, setLazyLoadedSrc] = useState();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const imageRef = useRef(null);
  const sharedImgProps = Object.assign({}, rest, {
    ref: imageRef,
    'data-lazy-src': src,
    src: lazyLoadedSrc
  });
  const lazyLoadedImgNode = /*#__PURE__*/_jsx(UIIntersectionObserver, {
    rootMargin: "60px 0px 60px 0px",
    renderContent: isIntersecting => /*#__PURE__*/_jsx(Img, Object.assign({}, sharedImgProps, {
      context: "document",
      $isIntersecting: isIntersecting,
      onClick: () => setLightboxOpen(true)
    })),
    onIntersectionChange: ({
      entries,
      observer
    }) => {
      callIfPossible(onIntersectionChange);
      if (entries[0].isIntersecting) {
        setLazyLoadedSrc(src);
        // Since we don't want to un-render, we can disconnect @see https://tools.hubteamqa.com/ui-library/show/UIComponents/observer/UIIntersectionObserver#uiintersectionobserver/lazy-load
        observer.disconnect();
      }
    }
  });
  return /*#__PURE__*/_jsx(ResponsiveRenderer, {
    renderDesktop: () => /*#__PURE__*/_jsxs(_Fragment, {
      children: [lazyLoadedImgNode, lightboxOpen && /*#__PURE__*/_jsxs(UIModal, {
        "data-test-id": "lightbox-modal",
        width: "100vw",
        centered: true,
        size: "tall",
        use: "lightbox"
        // @ts-expect-error type incorrect
        ,
        onClick: () => {
          setLightboxOpen(false);
        },
        children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
          detached: true,
          size: "lg"
        }), /*#__PURE__*/_jsx(UIOverlayContent, {
          ContentWrapper: OverlayContentWrapper,
          children: /*#__PURE__*/_jsx(Img, Object.assign({}, sharedImgProps, {
            context: "modal",
            $isIntersecting: true
          }))
        })]
      })]
    }),
    renderMobile: () => lazyLoadedImgNode
  });
}
export default Image;